import el from "@cypherpotato/el";
import page from "page";
import { api } from "../../api";
import { Button, DangerButton } from "../../components/button";
import { CommandBar } from "../../components/command-bar";
import { Dashboard, DashboardTitle } from "../../components/dashboard";
import { Dropdown, DropdownItem } from "../../components/dropdown";
import { Editor, EditorDescription, EditorTitle } from "../../components/editor";
import { FormControl, Select, TextInput } from "../../components/form";
import { Icon } from "../../components/icon";
import { Content, Row, Section, SmallContainer } from "../../components/layout";
import { ExternalLink } from "../../components/link";
import { SpecializedControl } from "../../components/specializedObject";
import { TabControl } from "../../components/tabcontrol";
import { DefaultRow, Table, TableActionCell, TableBody, TableCell, TableHeader, TableHeaderActionCell, TableHeaderCell, TableRow } from "../../components/table";
import { BlockCodeWrapper, CheckWrapper, Heading, MutedText, Paragraph } from "../../components/text";
import { Warning } from "../../components/warn";
import { getSession, putSession } from "../../session";
import { displayAlert, displayFormControl } from "../../ui/alert";
import { copyToClipboard } from "../../ui/clipboard";
import { showFlyout } from "../../ui/flyout";
import { formatDateTime } from "../../ui/format";
import { runOperation } from "../../ui/operation";
import { ellipsis } from "../../util";

export default function () {

    const session = getSession(true);
    const apiKeys = el.state.create(null);

    function getAccountPayload(overrides = {}) {
        const name = overrides.name ?? session.data.name;
        const params = session.data.parameters ?? {};
        return {
            name,
            parameters: {
                automaticJsonHealing: params.automaticJsonHealing ?? true,
                ...overrides.parameters
            }
        };
    }

    async function updateAccount(overrides = {}) {
        const payload = getAccountPayload(overrides);
        await api.putJson("/api/v1/accounts", payload);
        session.data.name = payload.name;
        session.data.parameters = payload.parameters;
        putSession(session);
    }

    function getApiKeys() {
        runOperation("Getting account keys...", async () => {
            const result = await api.getJson("/api/v1/accounts/api-key");
            apiKeys.value = result.data;
        });
    }

    getApiKeys();

    return Dashboard(
        DashboardTitle(
            "My account",
            "Manage your profile and API keys."
        ),
        Section(
            TabControl([
                {
                    title: "Account information",
                    contents: el.fragment(
                        Section(
                            Editor(
                                {
                                    id: "accountName",
                                    value: session.data.name,
                                    renderValue(value) {
                                        return Button(value || MutedText("None"));
                                    },
                                    manipulateValue(value, callback) {
                                        displayFormControl({
                                            title: this.editorTitle,
                                            message: this.editorParagraphs,
                                            formControlItem: TextInput(null, "Account name", { value }),
                                            onConfirm(newName) {
                                                runOperation("Updating account...", async () => {
                                                    await updateAccount({ name: newName });
                                                    callback(newName);
                                                });
                                            },
                                            validate: [
                                                [x => x.length > 3, "The account name must be at least 3 characters long."]
                                            ]
                                        })
                                    }
                                },
                                EditorDescription(
                                    EditorTitle("Account name"),
                                    Paragraph("Edit the name shown inside the application.")
                                )
                            ),
                            Editor(
                                {
                                    id: "accountEmail",
                                    value: session.data.email,
                                    readonly: true,
                                    renderValue(value) {
                                        return Button(value || MutedText("None"));
                                    },
                                    manipulateValue: null
                                },
                                EditorDescription(
                                    EditorTitle("Account e-mail"),
                                    Paragraph("This e-mail is used to identify your account.")
                                )
                            )
                        ),
                        Section(
                            Editor(
                                {
                                    id: "automaticJsonHealing",
                                    value: session.data.parameters?.automaticJsonHealing ?? true,
                                    renderValue(value) {
                                        return CheckWrapper(value, "enabled", "disabled");
                                    },
                                    manipulateValue(value, callback) {
                                        const newValue = !value;
                                        runOperation("Updating account...", async () => {
                                            await updateAccount({ parameters: { automaticJsonHealing: newValue } });
                                            callback(newValue);
                                        });
                                    }
                                },
                                EditorDescription(
                                    EditorTitle("Automatic JSON healing"),
                                    Paragraph("Automatically attempt to fix malformed JSON responses from AI models.")
                                )
                            ),
                            Editor(
                                {
                                    id: "accountPfp",
                                    value: true,
                                    renderValue() {
                                        return Button("Go to Gravatar", Icon("arrow-right-line"))
                                    },
                                    manipulateValue() {
                                        window.open("https://gravatar.com/", "_blank");
                                    }
                                },
                                EditorDescription(
                                    EditorTitle("Profile picture"),
                                    Paragraph("Your profile picture is provided by ", ExternalLink("Gravatar", "https://gravatar.com/"), ".")
                                )
                            ),
                            Editor(
                                {
                                    id: "accountLogout",
                                    value: true,
                                    renderValue() {
                                        return Button("Logout", {
                                            style: {
                                                color: "var(--warn)"
                                            }
                                        })
                                    },
                                    manipulateValue() {
                                        localStorage.clear();
                                        page("/")
                                    }
                                },
                                EditorDescription(
                                    EditorTitle("Logout"),
                                    Paragraph("End the current browser session.")
                                )
                            )
                        )
                    )
                },
                {
                    title: "API Keys",
                    contents: el.fragment(
                        Heading(
                            "API Keys",
                            Paragraph("Configure keys used by OpenAI-compatible clients and API calls.")
                        ),
                        el.state.listening([apiKeys], () =>
                            Content(
                                Section(
                                    Row(
                                        Button(Icon("add-line"),
                                            "Create a new key",
                                            {
                                                onClick() {
                                                    displayFormControl({
                                                        title: "New API key",
                                                        message: "Enter the data below to create a key.",
                                                        formControlItem: SpecializedControl({
                                                            fields: [
                                                                {
                                                                    name: "label",
                                                                    label: "Key name",
                                                                    control: TextInput(null, "My key")
                                                                },
                                                                {
                                                                    name: "duration",
                                                                    label: "Key expiration",
                                                                    control: Select({
                                                                        items: [
                                                                            { text: "7d", value: "7" },
                                                                            { text: "30d", value: "30" },
                                                                            { text: "90d", value: "90" },
                                                                            { text: "Never", value: "-1" }
                                                                        ]
                                                                    })
                                                                }
                                                            ]
                                                        }),
                                                        onConfirm: value => {
                                                            runOperation("Creating API key...", async () => {
                                                                const response = await api.postJson("/api/v1/accounts/api-key", value);
                                                                if (response.data?.grantKey) {
                                                                    displayAlert({
                                                                        title: "Key created",
                                                                        message: el.fragment(
                                                                            Paragraph("Your API key was generated."),
                                                                            BlockCodeWrapper("Generated API Key", response.data.grantKey),
                                                                            Warning(
                                                                                Paragraph("Store this key now. It will not be shown again.")
                                                                            )
                                                                        ),
                                                                        buttons: [
                                                                            {
                                                                                label: "OK",
                                                                                onClick(event) {
                                                                                    event.target.closest("dialog").dismiss();
                                                                                    getApiKeys();
                                                                                }
                                                                            }
                                                                        ]
                                                                    })
                                                                }
                                                            });
                                                        }
                                                    })
                                                }
                                            }
                                        )
                                    )
                                ),
                                Section(
                                    Table(
                                        TableHeader(
                                            TableHeaderCell("Key"),
                                            TableHeaderCell("Key name"),
                                            TableHeaderCell("Expiration"),
                                            TableHeaderCell("Last used"),
                                            TableHeaderActionCell("Actions"),
                                        ),
                                        TableBody(
                                            apiKeys.value?.apiKeys.map(k =>
                                                TableRow(
                                                    TableCell(k.key),
                                                    TableCell(k.label || MutedText("No name")),
                                                    TableCell(k.expiresAt && formatDateTime(k.expiresAt) || MutedText("No expiration")),
                                                    TableCell(k.usedAt && formatDateTime(k.usedAt) || MutedText("Never used")),
                                                    TableActionCell(
                                                        Dropdown(
                                                            Icon("more-2-line"),
                                                            DropdownItem("Delete", Icon("delete-bin-7-line"), {
                                                                onClick() {
                                                                    showFlyout(
                                                                        Paragraph("Are you sure you want to delete this key?"),
                                                                        Paragraph("Services using this key will stop working."),
                                                                        DangerButton(
                                                                            "Yes, delete key",
                                                                            {
                                                                                onClick() {
                                                                                    runOperation("Deleting key...", async () => {
                                                                                        await api.deleteJson(`/api/v1/accounts/api-key/${k.id}`);
                                                                                        getApiKeys();
                                                                                    });
                                                                                }
                                                                            }
                                                                        )
                                                                    )
                                                                }
                                                            })
                                                        )
                                                    )
                                                )
                                            )?.nullWhenEmpty() || DefaultRow("You have not created any API keys.")
                                        )
                                    )
                                )
                            )
                        )
                    )
                },
                {
                    title: "Hook key",
                    contents: el.fragment(
                        Heading(
                            "Hook key",
                            Paragraph("The hook key defines the secret for reverse calls from this instance to your services.")
                        ),
                        el.state.listening([apiKeys], () =>
                            Content(
                                SmallContainer(
                                    Section(
                                        FormControl(
                                            "Hook key",
                                            TextInput(null, "not generated", { readonly: true, value: apiKeys.value?.salt && ellipsis(apiKeys.value?.salt, 12) })
                                        )
                                    ),
                                    Section(
                                        CommandBar(
                                            Button(
                                                Icon("loop-right-line"),
                                                "Reset hook key",
                                                {
                                                    onClick() {
                                                        showFlyout(
                                                            Paragraph("Are you sure you want to reset the hook key?"),
                                                            Paragraph("Services using the current key will need to be updated."),
                                                            DangerButton(
                                                                "Reset hook key",
                                                                {
                                                                    onClick() {
                                                                        runOperation("Resetting hook key...", async () => {
                                                                            await api.postJson(`/api/v1/accounts/roll-salt`);
                                                                            getApiKeys();
                                                                        });
                                                                    }
                                                                }
                                                            )
                                                        )
                                                    }
                                                }
                                            ),
                                            Button(
                                                Icon("file-copy-line"),
                                                "Copy",
                                                {
                                                    onClick() {
                                                        copyToClipboard(apiKeys.value?.salt)
                                                    }
                                                }
                                            )
                                        )
                                    )
                                )
                            )
                        )
                    )
                }
            ])
        )
    )
}
